/**
 * OnlyFans' unread chats per Account, for the badges on the Accounts list
 * (ADR 0037). The server's counts (live sockets only) every minute, plus the
 * Keeper's changes as they happen through the Workspace socket.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { api } from '../api/client';

/** How often the counts are re-read while the tab is visible (one cheap DB read). */
export const UNREAD_CHATS_REFRESH_MS = 60_000;

let counts: Readonly<Record<string, number>> = {};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

/** One Account's count changed (the Workspace socket). */
export function setUnreadChats(accountId: string, count: number): void {
  if (!accountId || !Number.isInteger(count) || count < 0 || counts[accountId] === count) return;
  counts = { ...counts, [accountId]: count };
  emit();
}

/** The server's full answer: Accounts it leaves out have no live socket, so no badge. */
export function replaceUnreadChats(next: Record<string, number>): void {
  const clean: Record<string, number> = {};
  for (const [id, n] of Object.entries(next ?? {})) if (Number.isInteger(n) && n >= 0) clean[id] = n;
  counts = clean;
  emit();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useUnreadChats(): Readonly<Record<string, number>> {
  return useSyncExternalStore(subscribe, () => counts);
}

/** Keeps the counts fresh while mounted (the shell mounts it once). */
export function useUnreadChatsFeed(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      api.getUnreadChats().then(
        (next) => alive && replaceUnreadChats(next),
        () => {}, // keep what we have; the next tick tries again
      );
    };
    load();
    const timer = window.setInterval(load, UNREAD_CHATS_REFRESH_MS);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
    };
  }, [enabled]);
}
